import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../environments/environment';

export type CallState = 'idle' | 'ringing-out' | 'ringing-in' | 'connecting' | 'connected' | 'ended';

export interface IncomingCallInfo {
  callId: string;
  callerId: string;
  callerName: string;
  callerRole: string;
}

@Injectable({
  providedIn: 'root'
})
export class CallService {
  callState$ = new BehaviorSubject<CallState>('idle');
  remoteStream$ = new BehaviorSubject<MediaStream | null>(null);
  localStream$ = new BehaviorSubject<MediaStream | null>(null);
  incomingCall$ = new Subject<IncomingCallInfo>();
  availableAdmins$ = new BehaviorSubject<{ adminId: string; name: string }[]>([]);
  screenSharing$ = new BehaviorSubject<boolean>(false);
  isLocalMuted$ = new BehaviorSubject<boolean>(false);
  isLocalCameraOff$ = new BehaviorSubject<boolean>(false);

  private socket: Socket | null = null;
  private peerConnection: RTCPeerConnection | null = null;
  private localStream: MediaStream | null = null;
  private currentCallId: string | null = null;
  private currentAdminId: string | null = null;
  private currentCallerName: string = '';
  private currentCallerRole: string = '';
  private screenTrack: MediaStreamTrack | null = null;

  private readonly configuration: RTCConfiguration = {
    iceServers: [
      {
        urls: 'turn:a.relay.metered.ca:80',
        username: '0ed1b4fdac3609a8397799ff',
        credential: 'RP5dQKcnuNusMqcc',
      },
      {
        urls: 'turn:a.relay.metered.ca:80?transport=tcp',
        username: '0ed1b4fdac3609a8397799ff',
        credential: 'RP5dQKcnuNusMqcc',
      },
      {
        urls: 'turn:a.relay.metered.ca:443',
        username: '0ed1b4fdac3609a8397799ff',
        credential: 'RP5dQKcnusMqcc',
      },
      {
        urls: 'turn:a.relay.metered.ca:443?transport=tcp',
        username: '0ed1b4fdac3609a8397799ff',
        credential: 'RP5dQKcnuNusMqcc',
      },
      {
        urls: ['stun:stun1.l.google.com:19302', 'stun:stun2.l.google.com:19302', 'stun:a.relay.metered.ca:80'],
      },
    ],
    iceCandidatePoolSize: 10,
  };

  connect(adminId: string, role: string, name: string): void {
    if (this.socket?.connected) {
      return;
    }

    const socketUrl = environment.baseUrl.replace('/api/v1/', '');
    const token = localStorage.getItem('token');

    this.currentAdminId = adminId;
    this.currentCallerName = name;
    this.currentCallerRole = role;

    this.socket = io(socketUrl, {
      auth: { token },
      transports: ['websocket']
    });

    this.socket.on('connect', () => {
      // support:join is emitted explicitly by joinSupport() — not auto-emitted on connect
    });

    this.socket.on('support:available-admins', (admins: { adminId: string; name: string }[]) => {
      this.availableAdmins$.next(admins);
    });

    this.socket.on('support:admin-available', (admin: { adminId: string; name: string }) => {
      const current = this.availableAdmins$.getValue();
      if (!current.find(a => a.adminId === admin.adminId)) {
        this.availableAdmins$.next([...current, admin]);
      }
    });

    this.socket.on('support:admin-unavailable', (data: { adminId: string }) => {
      const current = this.availableAdmins$.getValue();
      this.availableAdmins$.next(current.filter(a => a.adminId !== data.adminId));
    });

    this.socket.on('support:incoming-call', (data: IncomingCallInfo) => {
      this.currentCallId = data.callId;
      this.callState$.next('ringing-in');
      this.incomingCall$.next(data);
    });

    this.socket.on('support:call-accepted', () => {
      this.callState$.next('connecting');
      this.createOffer();
    });

    this.socket.on('support:call-rejected', () => {
      this.callState$.next('ended');
      setTimeout(() => {
        this.callState$.next('idle');
        this.cleanup();
      }, 2000);
    });

    this.socket.on('support:call-ended', () => {
      this.callState$.next('ended');
      setTimeout(() => {
        this.callState$.next('idle');
        this.cleanup();
      }, 2000);
    });

    this.socket.on('support:offer', (data: { sdp: RTCSessionDescriptionInit }) => {
      this.handleOffer(data.sdp);
    });

    this.socket.on('support:answer', (data: { sdp: RTCSessionDescriptionInit }) => {
      this.handleAnswer(data.sdp);
    });

    this.socket.on('support:ice-candidate', (data: { candidate: RTCIceCandidateInit }) => {
      this.handleIceCandidate(data.candidate);
    });

    this.socket.on('support:screen-share-started', () => {
      console.log('Remote side started screen sharing');
    });

    this.socket.on('support:screen-share-ended', () => {
      console.log('Remote side stopped screen sharing');
    });
  }

  /**
   * Ensures the socket is connected AND emits support:join.
   * Safe to call multiple times: if already connected it emits support:join directly;
   * if not connected it calls connect() which will emit on the connect event.
   * Used by both:
   *  - store managers (role != 'admin') to request the current available-admins list
   *  - super admins (role == 'admin') to announce themselves as available
   */
  joinSupport(adminId: string, role: string, name: string): void {
    this.currentAdminId = adminId;
    this.currentCallerName = name;
    this.currentCallerRole = role;

    if (this.socket?.connected) {
      this.socket.emit('support:join', { adminId, role, name });
    } else {
      this.connect(adminId, role, name);
      this.socket?.once('connect', () => {
        this.socket?.emit('support:join', { adminId, role, name });
      });
    }
  }

  /**
   * Explicitly removes this admin from onlineAdmins on the server.
   * Used when the availability toggle is switched OFF.
   */
  leaveSupport(): void {
    if (this.socket?.connected && this.currentAdminId) {
      this.socket.emit('support:leave', { adminId: this.currentAdminId });
    }
  }

  requestCall(targetAdminId: string): void {
    const callId = 'call-' + Date.now();
    this.currentCallId = callId;
    this.callState$.next('ringing-out');

    navigator.mediaDevices.getUserMedia({ video: true, audio: true }).then(stream => {
      this.localStream = stream;
      this.localStream$.next(stream);

      this.socket?.emit('support:call-request', {
        callId,
        callerId: this.currentAdminId,
        callerName: this.currentCallerName,
        callerRole: this.currentCallerRole,
        targetAdminId
      });
    }).catch(err => {
      console.error('Error accessing media devices:', err);
      this.callState$.next('idle');
    });
  }

  acceptCall(callId: string): void {
    this.callState$.next('connecting');

    const acquireMedia = this.localStream
      ? Promise.resolve(this.localStream)
      : navigator.mediaDevices.getUserMedia({ video: true, audio: true });

    acquireMedia.then(stream => {
      if (!this.localStream) {
        this.localStream = stream;
        this.localStream$.next(stream);
      }
      this.createPeerConnection();
      this.socket?.emit('support:call-accept', { callId });
    }).catch(err => {
      console.error('Error accessing media devices:', err);
      this.callState$.next('idle');
    });
  }

  rejectCall(callId: string, reason?: string): void {
    this.socket?.emit('support:call-reject', { callId, reason: reason || 'Rejected' });
    this.callState$.next('idle');
  }

  endCall(): void {
    this.socket?.emit('support:call-end', { callId: this.currentCallId });
    this.callState$.next('ended');
    setTimeout(() => {
      this.callState$.next('idle');
      this.cleanup();
    }, 2000);
  }

  toggleMute(): void {
    if (this.localStream) {
      const audioTracks = this.localStream.getAudioTracks();
      const nowMuted = !this.isLocalMuted$.getValue();
      audioTracks.forEach(track => (track.enabled = !nowMuted));
      this.isLocalMuted$.next(nowMuted);
    }
  }

  toggleCamera(): void {
    if (this.localStream) {
      const videoTracks = this.localStream.getVideoTracks();
      const nowOff = !this.isLocalCameraOff$.getValue();
      videoTracks.forEach(track => (track.enabled = !nowOff));
      this.isLocalCameraOff$.next(nowOff);
    }
  }

  async startScreenShare(): Promise<void> {
    try {
      const screenStream = await (navigator.mediaDevices as any).getDisplayMedia({ video: true });
      this.screenTrack = screenStream.getVideoTracks()[0];

      if (this.peerConnection) {
        const sender = this.peerConnection.getSenders().find(s => s.track?.kind === 'video');
        if (sender && this.screenTrack) {
          await sender.replaceTrack(this.screenTrack);
        }
      }

      this.screenSharing$.next(true);
      this.socket?.emit('support:screen-share-start', { callId: this.currentCallId });

      if (this.screenTrack) {
        this.screenTrack.onended = () => this.stopScreenShare();
      }
    } catch (err) {
      console.error('Error starting screen share:', err);
    }
  }

  stopScreenShare(): void {
    if (!this.localStream || !this.peerConnection) return;

    const cameraTrack = this.localStream.getVideoTracks()[0];
    const sender = this.peerConnection.getSenders().find(s => s.track?.kind === 'video');

    if (sender && cameraTrack) {
      sender.replaceTrack(cameraTrack).catch(err => console.error('Error reverting camera track:', err));
    }

    if (this.screenTrack) {
      this.screenTrack.stop();
      this.screenTrack = null;
    }

    this.screenSharing$.next(false);
    this.socket?.emit('support:screen-share-end', { callId: this.currentCallId });
  }

  private createPeerConnection(): void {
    this.peerConnection = new RTCPeerConnection(this.configuration);

    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        this.socket?.emit('support:ice-candidate', {
          callId: this.currentCallId,
          candidate: event.candidate
        });
      }
    };

    this.peerConnection.ontrack = (event) => {
      this.remoteStream$.next(event.streams[0]);
    };

    if (this.localStream) {
      this.localStream.getTracks().forEach(track => {
        this.peerConnection!.addTrack(track, this.localStream!);
      });
    }
  }

  private async createOffer(): Promise<void> {
    this.createPeerConnection();
    if (!this.peerConnection) return;

    try {
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);
      this.socket?.emit('support:offer', { callId: this.currentCallId, sdp: offer });
    } catch (err) {
      console.error('Error creating offer:', err);
    }
  }

  private async handleOffer(sdp: RTCSessionDescriptionInit): Promise<void> {
    if (!this.peerConnection) {
      this.createPeerConnection();
    }
    if (!this.peerConnection) return;

    try {
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(sdp));
      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);
      this.socket?.emit('support:answer', { callId: this.currentCallId, sdp: answer });
    } catch (err) {
      console.error('Error handling offer:', err);
    }
  }

  private async handleAnswer(sdp: RTCSessionDescriptionInit): Promise<void> {
    if (!this.peerConnection) return;

    try {
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(sdp));
      this.callState$.next('connected');
    } catch (err) {
      console.error('Error handling answer:', err);
    }
  }

  private async handleIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (this.peerConnection) {
      try {
        await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        console.error('Error adding ICE candidate:', err);
      }
    }
  }

  private cleanup(): void {
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
      this.localStream$.next(null);
    }

    if (this.screenTrack) {
      this.screenTrack.stop();
      this.screenTrack = null;
    }

    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    this.currentCallId = null;
    this.remoteStream$.next(null);
    this.screenSharing$.next(false);
    this.isLocalMuted$.next(false);
    this.isLocalCameraOff$.next(false);
  }
}
